import { useCallback } from 'react';
import { useCanvas } from '../contexts/CanvasContext';
import { useDesignerStore } from '../stores';
import { useHistoryStore } from '../stores/historyStore';
import type { ShapeType } from '../types/tools';

// Import fabric for side effects
import 'fabric';

// Apply white filter for laser mode
function applyLaserWhiteFilter(img: fabric.Image) {
  const f = window.fabric;

  // Create a filter that converts all non-transparent pixels to white
  const whiteFilter = new f.Image.filters.BlendColor({
    color: '#FFFFFF',
    mode: 'tint',
    alpha: 1,
  });

  img.filters = img.filters || [];
  // Remove existing laser filter if present
  img.filters = img.filters.filter((filter: any) => !filter.isLaserFilter);
  // Add laser filter
  (whiteFilter as any).isLaserFilter = true;
  img.filters.push(whiteFilter);
  img.applyFilters();
}

// Helper to generate unique IDs
const generateId = () => `obj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useCanvasOperations = () => {
  const { canvas } = useCanvas();
  const { laserOnly } = useDesignerStore();
  const { saveSnapshot } = useHistoryStore();

  // Helper to ensure mask overlays stay on top
  const bringMaskToFront = useCallback(() => {
    if (!canvas) return;
    const maskOverlays = canvas.getObjects().filter((obj) => obj.isMaskOverlay);
    maskOverlays.forEach((mask) => canvas.bringToFront(mask));
  }, [canvas]);

  const saveToHistory = useCallback(() => {
    if (!canvas) return;
    const state = JSON.stringify(canvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay']));
    saveSnapshot(state);
  }, [canvas, saveSnapshot]);

  // Add text object
  const addText = useCallback((text = 'Your Text') => {
    if (!canvas) return;

    const f = window.fabric;
    const textObj = new f.Textbox(text, {
      left: canvas.width! / 2,
      top: canvas.height! / 2,
      fontFamily: 'Inter',
      fontSize: 36,
      fill: laserOnly ? '#FFFFFF' : '#000000',
      originX: 'center',
      originY: 'center',
      textAlign: 'center',
      width: 300,
      splitByGrapheme: true,
    });
    textObj.set('id', generateId());

    // Apply clipPath from analyzed mask
    const clipPath = (canvas as any)._designClipPath;
    if (clipPath) {
      textObj.clipPath = clipPath;
    }

    canvas.add(textObj);
    bringMaskToFront(); // Ensure mask stays on top
    canvas.setActiveObject(textObj);
    textObj.enterEditing();
    textObj.selectAll();
    canvas.renderAll();
    saveToHistory();
  }, [canvas, laserOnly, saveToHistory, bringMaskToFront]);

  // Add shape
  const addShape = useCallback((shapeType: ShapeType) => {
    if (!canvas) return;

    const f = window.fabric;
    const centerX = canvas.width! / 2;
    const centerY = canvas.height! / 2;
    const fillColor = laserOnly ? '#FFFFFF' : '#8BC34A';

    let shape: fabric.Object;

    switch (shapeType) {
      case 'rect':
        shape = new f.Rect({
          left: centerX,
          top: centerY,
          width: 150,
          height: 100,
          fill: fillColor,
          originX: 'center',
          originY: 'center',
        });
        break;
      case 'circle':
        shape = new f.Circle({
          left: centerX,
          top: centerY,
          radius: 75,
          fill: fillColor,
          originX: 'center',
          originY: 'center',
        });
        break;
      case 'triangle':
        shape = new f.Triangle({
          left: centerX,
          top: centerY,
          width: 150,
          height: 130,
          fill: fillColor,
          originX: 'center',
          originY: 'center',
        });
        break;
      case 'line':
        shape = new f.Line([centerX - 75, centerY, centerX + 75, centerY], {
          stroke: fillColor,
          strokeWidth: 4,
          originX: 'center',
          originY: 'center',
        });
        break;
      default:
        return;
    }
    shape.set('id', generateId());

    // Apply clipPath from analyzed mask
    const clipPath = (canvas as any)._designClipPath;
    if (clipPath) {
      shape.clipPath = clipPath;
    }

    canvas.add(shape);
    bringMaskToFront(); // Ensure mask stays on top
    canvas.setActiveObject(shape);
    canvas.renderAll();
    saveToHistory();
  }, [canvas, laserOnly, saveToHistory, bringMaskToFront]);

  // Add image from URL with optional original file URL (for converted PSD/AI/PDF files)
  const addImageFromUrl = useCallback((url: string, originalUrl?: string) => {
    if (!canvas) return;

    const f = window.fabric;
    const { printArea } = useDesignerStore.getState();

    f.Image.fromURL(url, (img) => {
      if (!img) return;

      // Scale image to fit within print area (80% like legacy)
      const maxSize = printArea
        ? Math.min(printArea.width, printArea.height) * 0.8
        : Math.min(canvas.width!, canvas.height!) * 0.6;
      const scale = Math.min(maxSize / img.width!, maxSize / img.height!);

      img.set({
        id: generateId(),
        left: canvas.width! / 2,
        top: canvas.height! / 2,
        scaleX: scale,
        scaleY: scale,
        originX: 'center',
        originY: 'center',
      });

      // Store the original file URL (for PSD/AI/PDF - the pre-conversion file)
      // This is what vendors will download, not the rendered PNG
      if (originalUrl) {
        (img as any).uploadedUrl = originalUrl;
      }

      // Apply clipPath from analyzed mask
      const clipPath = (canvas as any)._designClipPath;
      if (clipPath) {
        img.clipPath = clipPath;
      }

      // Apply laser white filter if in laser mode
      if (laserOnly) {
        applyLaserWhiteFilter(img);
      }

      canvas.add(img);
      bringMaskToFront(); // Ensure mask stays on top
      canvas.setActiveObject(img);
      canvas.renderAll();
      saveToHistory();
    }, { crossOrigin: 'anonymous' });
  }, [canvas, laserOnly, saveToHistory, bringMaskToFront]);

  // Add image from File — renders the LOCAL file instantly, then uploads to R2
  // in the background and swaps to the permanent (optimized) URL once ready.
  // The upload must never block the preview: the image appears immediately.
  const addImageFromFile = useCallback(async (
    file: File,
    onUploaded?: (url: string) => void
  ): Promise<void> => {
    if (!canvas) return;

    const f = window.fabric;
    const { printArea } = useDesignerStore.getState();

    // Read file as base64 for IMMEDIATE display (no server round-trip)
    const dataUrl = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.readAsDataURL(file);
    });

    const imageId = generateId();

    // 1) Render instantly from the local data URL
    await new Promise<void>((resolve) => {
      f.Image.fromURL(dataUrl, (img) => {
        if (!img) { resolve(); return; }

        // Scale image to fit within print area (80% like legacy)
        const maxSize = printArea
          ? Math.min(printArea.width, printArea.height) * 0.8
          : Math.min(canvas.width!, canvas.height!) * 0.6;
        const scale = Math.min(maxSize / img.width!, maxSize / img.height!);

        img.set({
          id: imageId,
          left: canvas.width! / 2,
          top: canvas.height! / 2,
          scaleX: scale,
          scaleY: scale,
          originX: 'center',
          originY: 'center',
        });

        // Apply clipPath from analyzed mask
        const clipPath = (canvas as any)._designClipPath;
        if (clipPath) {
          img.clipPath = clipPath;
        }

        // Apply laser white filter if in laser mode
        if (laserOnly) {
          applyLaserWhiteFilter(img);
        }

        canvas.add(img);
        bringMaskToFront(); // Ensure mask stays on top
        canvas.setActiveObject(img);
        canvas.renderAll();
        saveToHistory();
        resolve();
      }, { crossOrigin: 'anonymous' });
    });

    // 2) Upload to R2 in the background, then attach + swap to the permanent URL.
    //    Non-fatal on failure — the local data URL stays on the canvas.
    (async () => {
      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formData,
        });

        const result = await response.json();
        if (!result.success || !result.url) return;

        const obj: any = canvas.getObjects().find((o: any) => o.id === imageId);
        if (!obj) return;

        // The R2 URL is what vendors download / what the saved design references.
        obj.uploadedUrl = result.url;
        onUploaded?.(result.url);

        // Swap the visual to the optimized R2 image, but only AFTER verifying it
        // loads — so a CDN hiccup can never blank out the already-visible preview.
        if (typeof obj.setSrc === 'function') {
          const probe = new Image();
          probe.crossOrigin = 'anonymous';
          probe.onload = () => {
            const prevW = obj.getScaledWidth();
            const prevH = obj.getScaledHeight();
            obj.setSrc(result.url, () => {
              // Preserve on-canvas size in case R2 resized the image
              if (obj.width && obj.height) {
                obj.scaleX = prevW / obj.width;
                obj.scaleY = prevH / obj.height;
              }
              bringMaskToFront();
              canvas.renderAll();
              saveToHistory();
            }, { crossOrigin: 'anonymous' });
          };
          probe.src = result.url;
        }
      } catch {
        // Keep the local data URL — the image stays usable even if upload failed.
      }
    })();
  }, [canvas, laserOnly, saveToHistory, bringMaskToFront]);

  // Delete selected objects
  const deleteSelected = useCallback(() => {
    if (!canvas) return;

    const activeObjects = canvas.getActiveObjects();
    if (activeObjects.length === 0) return;

    // Filter out system objects
    const objectsToDelete = activeObjects.filter(
      (obj) => !obj.isGuide && !obj.isMockup && !obj.isBaseImage && !obj.isMaskOverlay
    );

    if (objectsToDelete.length > 0) {
      canvas.discardActiveObject();
      objectsToDelete.forEach((obj) => canvas.remove(obj));
      canvas.renderAll();
      saveToHistory();
    }
  }, [canvas, saveToHistory]);

  // Copy selected objects
  const copySelected = useCallback(() => {
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    activeObject.clone((cloned: fabric.Object) => {
      (window as unknown as { _clipboard?: fabric.Object })._clipboard = cloned;
    }, ['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay']);
  }, [canvas]);

  // Paste clipboard
  const paste = useCallback(() => {
    if (!canvas) return;

    const clipboard = (window as unknown as { _clipboard?: fabric.Object })._clipboard;
    if (!clipboard) return;

    clipboard.clone((cloned: fabric.Object) => {
      cloned.set({
        id: generateId(),
        left: (cloned.left || 0) + 20,
        top: (cloned.top || 0) + 20,
        evented: true,
      });

      if (cloned.type === 'activeSelection') {
        // Handle multiple objects
        cloned.canvas = canvas;
        (cloned as fabric.ActiveSelection).forEachObject((obj: fabric.Object) => {
          obj.set({ id: generateId() });
          canvas.add(obj);
        });
        cloned.setCoords();
      } else {
        canvas.add(cloned);
      }

      // Update clipboard position for next paste
      (window as unknown as { _clipboard?: fabric.Object })._clipboard!.set({
        left: (clipboard.left || 0) + 20,
        top: (clipboard.top || 0) + 20,
      });

      bringMaskToFront(); // Ensure mask stays on top
      canvas.setActiveObject(cloned);
      canvas.renderAll();
      saveToHistory();
    }, ['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay']);
  }, [canvas, saveToHistory, bringMaskToFront]);

  // Duplicate selected
  const duplicateSelected = useCallback(() => {
    copySelected();
    setTimeout(() => paste(), 10);
  }, [copySelected, paste]);

  // Bring forward
  const bringForward = useCallback(() => {
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (!activeObject || activeObject.isGuide || activeObject.isMockup) return;

    canvas.bringForward(activeObject);
    canvas.renderAll();
    saveToHistory();
  }, [canvas, saveToHistory]);

  // Send backward
  const sendBackward = useCallback(() => {
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (!activeObject || activeObject.isGuide || activeObject.isMockup) return;

    canvas.sendBackwards(activeObject);
    canvas.renderAll();
    saveToHistory();
  }, [canvas, saveToHistory]);

  // Align object
  const alignObject = useCallback((alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    const canvasWidth = canvas.width!;
    const canvasHeight = canvas.height!;

    switch (alignment) {
      case 'left':
        activeObject.set({ left: activeObject.width! * (activeObject.scaleX || 1) / 2 });
        break;
      case 'center':
        activeObject.set({ left: canvasWidth / 2 });
        break;
      case 'right':
        activeObject.set({ left: canvasWidth - activeObject.width! * (activeObject.scaleX || 1) / 2 });
        break;
      case 'top':
        activeObject.set({ top: activeObject.height! * (activeObject.scaleY || 1) / 2 });
        break;
      case 'middle':
        activeObject.set({ top: canvasHeight / 2 });
        break;
      case 'bottom':
        activeObject.set({ top: canvasHeight - activeObject.height! * (activeObject.scaleY || 1) / 2 });
        break;
    }

    activeObject.setCoords();
    canvas.renderAll();
    saveToHistory();
  }, [canvas, saveToHistory]);

  return {
    addText,
    addShape,
    addImageFromUrl,
    addImageFromFile,
    deleteSelected,
    copySelected,
    paste,
    duplicateSelected,
    bringForward,
    sendBackward,
    alignObject,
  };
};

export default useCanvasOperations;
